import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type {
  Connector,
  ConnectorTestInput,
  ConnectorTestResult,
  CreateConnectorInput,
  McpDiscoverResult,
  OAuthStartResult,
  SlackActivityItem,
  UpdateConnectorInput,
} from '@/lib/api/types';

const connectorKeys = {
  all: () => ['connectors', getActiveTenantId()] as const,
  // The detail key is by `ref` -- a slug or a UUID (spec 0013, AC-9).
  detail: (ref: string) => ['connectors', getActiveTenantId(), ref] as const,
  slackActivity: (id: string, limit: number, offset: number) =>
    ['connectors', getActiveTenantId(), id, 'slack-activity', limit, offset] as const,
};

export function useConnectors() {
  return useQuery({
    queryKey: connectorKeys.all(),
    queryFn: () => apiFetch<Connector[]>('/connectors'),
  });
}

/** Resolve a connector by `ref` -- its slug or UUID (spec 0013, ADR-0002). */
export function useConnector(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? connectorKeys.detail(ref) : ['connectors', 'detail', 'none'],
    queryFn: () => apiFetch<Connector>(`/connectors/${ref}`),
    enabled: Boolean(ref),
  });
}

export function useCreateConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConnectorInput) =>
      apiFetch<Connector>('/connectors', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

export function useUpdateConnector(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateConnectorInput) =>
      apiFetch<Connector>(`/connectors/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

export function useDeleteConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/connectors/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

export function useTestConnector(id: string) {
  return useMutation({
    mutationFn: (input: ConnectorTestInput) =>
      apiFetch<ConnectorTestResult>(`/connectors/${id}/test`, { method: 'POST', body: input }),
  });
}

export function useDiscoverConnector(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<McpDiscoverResult>(`/connectors/${id}/discover`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

export function useStartOAuth(id: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch<OAuthStartResult>(`/connectors/${id}/oauth/start`, { method: 'POST' }),
  });
}

export function useDisconnectOAuth(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>(`/connectors/${id}/oauth/grant`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

// ----- Slack activity (spec 0011, FR-7) -----

export function useSlackActivity(
  connectorId: string | undefined,
  params: { limit?: number; offset?: number } = {},
) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  return useQuery({
    queryKey: connectorId
      ? connectorKeys.slackActivity(connectorId, limit, offset)
      : ['connectors', 'slack-activity', 'none'],
    queryFn: () =>
      apiFetch<SlackActivityItem[]>(
        `/connectors/${connectorId}/slack/activity?limit=${limit}&offset=${offset}`,
      ),
    enabled: Boolean(connectorId),
  });
}

// ----- Agent links -----

function agentConnectorsKey(agentId: string | undefined) {
  return ['agent-connectors', getActiveTenantId(), agentId] as const;
}

export function useAgentConnectors(agentId: string | undefined) {
  return useQuery({
    queryKey: agentConnectorsKey(agentId),
    queryFn: () => apiFetch<Connector[]>(`/agents/${agentId}/connectors`),
    enabled: Boolean(agentId),
  });
}

export function useAttachAgentConnector(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectorId: string) =>
      apiFetch<Connector>(`/agents/${agentId}/connectors`, {
        method: 'POST',
        body: { connector_id: connectorId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentConnectorsKey(agentId) });
    },
  });
}

export function useDetachAgentConnector(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectorId: string) =>
      apiFetch<void>(`/agents/${agentId}/connectors/${connectorId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentConnectorsKey(agentId) });
    },
  });
}
